import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { TextField } from '../../ui/components/TextField';
import { TextArea } from '../../ui/components/TextArea';
import { mintRewardsApi, type CreateMintRewardsInput } from '../../api/mint-rewards';
import type { ContestView, PrizeTier } from '../../api/sweepstakes';
import { Leaderboard } from '../sweepstakes/Leaderboard';
import { WalletAddress } from '../sweepstakes/WalletAddress';
import { useNotificationStore } from '../../store/notificationStore';

interface MintRewardsManagerProps {
  launchId: string;
  launchStatus: string;
}

interface PrizeTierInput {
  name: string;
  description: string;
  type: 'leaderboard' | 'raffle';
  winnerCount: number;
}

const localDateTimeToISO = (localDateTime: string): string => {
  return new Date(localDateTime).toISOString();
};

const isoToLocalDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function formatCountdownStr(target: string): string {
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return 'Now';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

function useCountdown(target: string | undefined): string {
  const [display, setDisplay] = useState(() => target ? formatCountdownStr(target) : '');
  useEffect(() => {
    if (!target) return;
    setDisplay(formatCountdownStr(target));
    const interval = setInterval(() => setDisplay(formatCountdownStr(target)), 60_000);
    return () => clearInterval(interval);
  }, [target]);
  return display;
}

// ============================================================================
// Main Component
// ============================================================================

export const MintRewardsManager: React.FC<MintRewardsManagerProps> = ({ launchId, launchStatus }) => {
  const [contestView, setContestView] = useState<ContestView | null>(null);
  const [pastContests, setPastContests] = useState<ContestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const { success: showSuccess, error: showError } = useNotificationStore();

  const fetchContest = async () => {
    setLoading(true);
    try {
      const data = await mintRewardsApi.getForLaunchManage(launchId);
      setContestView(data.contest ? data as ContestView : null);
      setPastContests(data.pastContests || []);
    } catch (err) {
      console.error('Failed to fetch mint rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContest();
  }, [launchId]);

  if (loading) {
    return <div className="text-sm text-subtext-color py-4">Loading mint contest data...</div>;
  }

  const isResolved = contestView?.contest.status === 'resolved';
  const allPastContests = isResolved && contestView
    ? [contestView, ...pastContests]
    : pastContests;

  if (mode === 'create') {
    return (
      <MintRewardsForm
        launchId={launchId}
        onSave={() => { setMode('view'); fetchContest(); }}
        onCancel={() => setMode('view')}
        showSuccess={showSuccess}
        showError={showError}
      />
    );
  }

  if (!contestView || isResolved) {
    return (
      <div className="space-y-6">
        <div className="p-4 border border-neutral-border rounded-lg">
          <h3 className="font-semibold text-default-font mb-2">Mint Contest</h3>
          <p className="text-sm text-subtext-color mb-3">
            {isResolved
              ? 'Your previous mint contest has been resolved. You can create a new one.'
              : 'Create a mint contest to incentivize minting. Each mint = 1 entry into a prize drawing.'}
          </p>
          <Button variant="brand-primary" onClick={() => setMode('create')}>
            Create Mint Contest
          </Button>
        </div>
        {allPastContests.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-subtext-color">Past Contests</h4>
            {allPastContests.map(cv => (
              <PastContestSummary key={cv.contest.id} contestView={cv} />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (mode === 'edit') {
    return (
      <MintRewardsForm
        launchId={launchId}
        existingContest={contestView}
        onSave={() => { setMode('view'); fetchContest(); }}
        onCancel={() => setMode('view')}
        showSuccess={showSuccess}
        showError={showError}
      />
    );
  }

  return (
    <div className="space-y-6">
      <MintRewardsManageView
        contestView={contestView}
        onRefresh={fetchContest}
        onEdit={() => setMode('edit')}
        showSuccess={showSuccess}
        showError={showError}
      />
      {pastContests.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-subtext-color">Past Contests</h4>
          {pastContests.map(cv => (
            <PastContestSummary key={cv.contest.id} contestView={cv} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Form (create + edit)
// ============================================================================

interface MintRewardsFormProps {
  launchId: string;
  existingContest?: ContestView;
  onSave: () => void;
  onCancel: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

const MintRewardsForm: React.FC<MintRewardsFormProps> = ({
  launchId, existingContest, onSave, onCancel, showSuccess, showError,
}) => {
  const contest = existingContest?.contest;
  const isEdit = !!contest;

  const now = Date.now();
  const startPassed = contest ? new Date(contest.starts_at).getTime() <= now : false;
  const endPassed = contest ? new Date(contest.ends_at).getTime() <= now : false;
  const isActive = contest?.status === 'active';

  const lockName = isActive;
  const lockDescription = isActive;
  const lockStart = startPassed;
  const lockEnd = endPassed;
  const lockTierStructure = startPassed;

  const [name, setName] = useState(contest?.name ?? '');
  const [description, setDescription] = useState(contest?.description ?? '');
  const [startsAt, setStartsAt] = useState(
    contest ? isoToLocalDateTime(contest.starts_at) : ''
  );
  const [endsAt, setEndsAt] = useState(
    contest ? isoToLocalDateTime(contest.ends_at) : ''
  );
  const [prizeTiers, setPrizeTiers] = useState<PrizeTierInput[]>(() => {
    if (existingContest?.prizeTiers?.length) {
      return existingContest.prizeTiers.map(t => ({
        name: t.name,
        description: t.description,
        type: t.type,
        winnerCount: t.winner_count,
      }));
    }
    return [{ name: 'Grand Prize', description: '', type: 'raffle', winnerCount: 1 }];
  });
  const [submitting, setSubmitting] = useState(false);

  const addPrizeTier = () => {
    setPrizeTiers([...prizeTiers, {
      name: `Prize ${prizeTiers.length + 1}`,
      description: '',
      type: 'raffle',
      winnerCount: 1,
    }]);
  };

  const removePrizeTier = (index: number) => {
    if (prizeTiers.length <= 1) return;
    setPrizeTiers(prizeTiers.filter((_, i) => i !== index));
  };

  const updatePrizeTier = (index: number, field: keyof PrizeTierInput, value: any) => {
    const updated = [...prizeTiers];
    updated[index] = { ...updated[index], [field]: value };
    setPrizeTiers(updated);
  };

  const handleSubmit = async () => {
    if (!name || !startsAt || !endsAt || prizeTiers.some(t => !t.description)) {
      showError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        const update: Record<string, any> = {};
        if (!lockName) update.name = name;
        if (!lockDescription) update.description = description || undefined;
        if (!lockStart) update.startsAt = localDateTimeToISO(startsAt);
        if (!lockEnd) update.endsAt = localDateTimeToISO(endsAt);
        update.prizeTiers = prizeTiers;
        await mintRewardsApi.update(contest!.id, update);
        showSuccess('Mint contest updated');
      } else {
        await mintRewardsApi.create({
          launchId,
          name,
          description: description || undefined,
          startsAt: localDateTimeToISO(startsAt),
          endsAt: localDateTimeToISO(endsAt),
          prizeTiers,
        });
        showSuccess('Mint contest created');
      }
      onSave();
    } catch (err: any) {
      showError(err?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 border border-neutral-border rounded-lg space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-default-font">
          {isEdit ? 'Edit Mint Contest' : 'Create Mint Contest'}
        </h3>
        <Button variant="neutral-secondary" size="small" onClick={onCancel}>Cancel</Button>
      </div>

      <div className="space-y-3">
        <TextField label="Contest Name" disabled={lockName}>
          <TextField.Input
            placeholder="e.g. Launch Day Raffle"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </TextField>

        <TextArea label="Description (optional)">
          <TextArea.Input
            placeholder="Describe the prizes and how winners will be selected..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={lockDescription}
          />
        </TextArea>

        <div className="grid grid-cols-2 gap-3">
          <TextField label={`Starts At (${userTimezone})`} disabled={lockStart}>
            <TextField.Input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </TextField>

          <TextField label={`Ends At (${userTimezone})`} disabled={lockEnd}>
            <TextField.Input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </TextField>
        </div>

        <p className="text-xs text-subtext-color">
          Tip: Set start time to your launch start so all mints count as entries.
        </p>

        {/* Prize Tiers */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-default-font">Prize Tiers</h4>
            {!lockTierStructure && (
              <Button variant="neutral-secondary" size="small" onClick={addPrizeTier}>
                + Add Tier
              </Button>
            )}
          </div>
          {prizeTiers.map((tier, i) => (
            <div key={i} className="p-3 border border-neutral-border rounded-md space-y-2 bg-default-background">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-default-font">Tier {i + 1}</span>
                {!lockTierStructure && prizeTiers.length > 1 && (
                  <button
                    onClick={() => removePrizeTier(i)}
                    className="text-xs text-error-600 hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <TextField label="Name" disabled={lockTierStructure}>
                  <TextField.Input
                    placeholder="e.g. Grand Prize"
                    value={tier.name}
                    onChange={(e) => updatePrizeTier(i, 'name', e.target.value)}
                  />
                </TextField>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-caption font-caption text-subtext-color mb-1 block">Type</label>
                    <select
                      value={tier.type}
                      onChange={(e) => updatePrizeTier(i, 'type', e.target.value)}
                      disabled={lockTierStructure}
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font text-sm"
                    >
                      <option value="raffle">Raffle (random)</option>
                      <option value="leaderboard">Leaderboard (most mints)</option>
                    </select>
                  </div>
                  <TextField label="Winners" disabled={lockTierStructure}>
                    <TextField.Input
                      type="number"
                      min={1}
                      value={String(tier.winnerCount)}
                      onChange={(e) => updatePrizeTier(i, 'winnerCount', parseInt(e.target.value) || 1)}
                    />
                  </TextField>
                </div>
              </div>
              <TextArea label="Prize Description">
                <TextArea.Input
                  placeholder="e.g. 3 XRP sent to winner's wallet"
                  value={tier.description}
                  onChange={(e) => updatePrizeTier(i, 'description', e.target.value)}
                />
              </TextArea>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          variant="brand-primary"
          onClick={handleSubmit}
          loading={submitting}
          disabled={submitting}
        >
          {isEdit ? 'Save Changes' : 'Create Mint Contest'}
        </Button>
        <Button variant="neutral-secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// ============================================================================
// Manage View (active contest)
// ============================================================================

interface MintRewardsManageViewProps {
  contestView: ContestView;
  onRefresh: () => void;
  onEdit: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
}

const MintRewardsManageView: React.FC<MintRewardsManageViewProps> = ({
  contestView, onRefresh, onEdit, showSuccess, showError,
}) => {
  const { contest, prizeTiers, stats, winners } = contestView;
  const [publishing, setPublishing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [proofLinks, setProofLinks] = useState<string[]>(contest.proof_links || []);
  const [newProofLink, setNewProofLink] = useState('');

  const startCountdown = useCountdown(contest.starts_at);
  const endCountdown = useCountdown(contest.ends_at);

  const statusBadge = () => {
    switch (contest.status) {
      case 'draft': return <Badge variant="warning">Draft</Badge>;
      case 'scheduled': return <Badge variant="warning">Scheduled</Badge>;
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'ended': return <Badge variant="neutral">Ended</Badge>;
      case 'resolved': return <Badge variant="neutral">Resolved</Badge>;
      default: return <Badge>{contest.status}</Badge>;
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await mintRewardsApi.publish(contest.id);
      showSuccess('Mint contest published');
      onRefresh();
    } catch (err: any) {
      showError(err?.message || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleDraw = async () => {
    setDrawing(true);
    try {
      await mintRewardsApi.drawWinners(contest.id);
      showSuccess('Winners drawn');
      onRefresh();
    } catch (err: any) {
      showError(err?.message || 'Failed to draw winners');
    } finally {
      setDrawing(false);
    }
  };

  const handleResolve = async () => {
    setResolving(true);
    try {
      await mintRewardsApi.resolve(contest.id, proofLinks.length > 0 ? proofLinks : undefined);
      showSuccess('Contest resolved');
      onRefresh();
    } catch (err: any) {
      showError(err?.message || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await mintRewardsApi.delete(contest.id);
      showSuccess('Contest deleted');
      onRefresh();
    } catch (err: any) {
      showError(err?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const addProofLink = () => {
    if (newProofLink && newProofLink.includes('bithomp.com')) {
      setProofLinks([...proofLinks, newProofLink]);
      setNewProofLink('');
    }
  };

  return (
    <div className="p-4 border border-neutral-border rounded-lg space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-default-font">{contest.name}</h3>
          {statusBadge()}
        </div>
        <div className="flex items-center gap-2">
          {['draft', 'scheduled'].includes(contest.status) && (
            <>
              <Button variant="neutral-secondary" size="small" onClick={onEdit}>Edit</Button>
              <Button variant="destructive-secondary" size="small" onClick={handleDelete} loading={deleting}>Delete</Button>
            </>
          )}
          {contest.status === 'active' && (
            <Button variant="neutral-secondary" size="small" onClick={onEdit}>Edit</Button>
          )}
        </div>
      </div>

      {contest.description && (
        <p className="text-sm text-subtext-color">{contest.description}</p>
      )}

      {/* Timeline */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-subtext-color">Starts:</span>{' '}
          <span className="text-default-font">
            {new Date(contest.starts_at).toLocaleString()}
            {!new Date(contest.starts_at).getTime() || new Date(contest.starts_at).getTime() > Date.now()
              ? ` (${startCountdown})` : ' (passed)'}
          </span>
        </div>
        <div>
          <span className="text-subtext-color">Ends:</span>{' '}
          <span className="text-default-font">
            {new Date(contest.ends_at).toLocaleString()}
            {new Date(contest.ends_at).getTime() > Date.now()
              ? ` (${endCountdown})` : ' (passed)'}
          </span>
        </div>
      </div>

      {/* Stats */}
      {['active', 'ended', 'resolved'].includes(contest.status) && (
        <div className="flex gap-6 text-sm">
          <div>
            <span className="text-subtext-color">Entries:</span>{' '}
            <span className="font-medium text-default-font">{stats.entries}</span>
          </div>
          <div>
            <span className="text-subtext-color">Participants:</span>{' '}
            <span className="font-medium text-default-font">{stats.participants}</span>
          </div>
        </div>
      )}

      {/* Prize Tiers */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-default-font">Prizes</h4>
        {prizeTiers.map((tier) => (
          <div key={tier.id} className="text-sm text-default-font pl-3 border-l-2 border-brand-500">
            <span className="font-medium">{tier.name}</span>
            {' — '}
            <span className="text-subtext-color">
              {tier.type === 'leaderboard'
                ? `Most mints (${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''})`
                : `Random raffle (${tier.winner_count} winner${tier.winner_count > 1 ? 's' : ''})`
              }
            </span>
            <br />
            <span>{tier.description}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      {contest.status === 'draft' && (
        <Button variant="brand-primary" onClick={handlePublish} loading={publishing}>
          Publish
        </Button>
      )}

      {/* Leaderboard */}
      {['active', 'ended', 'resolved'].includes(contest.status) && (
        <Leaderboard
          contestId={contest.id}
          frozen={['ended', 'resolved'].includes(contest.status)}
        />
      )}

      {/* Winners */}
      {winners && winners.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-default-font">Winners</h4>
          {winners.map((w) => (
            <div key={w.id} className="flex items-center gap-2 text-sm">
              <Badge variant="success">{w.tier_name}</Badge>
              <WalletAddress address={w.wallet_address} />
            </div>
          ))}
        </div>
      )}

      {/* Draw + Resolve */}
      {contest.status === 'ended' && !winners?.length && (
        <Button variant="brand-primary" onClick={handleDraw} loading={drawing}>
          Draw Winners
        </Button>
      )}

      {contest.status === 'ended' && winners && winners.length > 0 && (
        <div className="space-y-3 border-t border-neutral-border pt-4">
          <h4 className="text-sm font-semibold text-default-font">Resolve Contest</h4>
          <p className="text-xs text-subtext-color">
            Add Bithomp proof-of-payment links (optional), then resolve to finalize.
          </p>
          {proofLinks.map((link, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <a href={link} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline truncate">
                {link}
              </a>
              <button
                onClick={() => setProofLinks(proofLinks.filter((_, j) => j !== i))}
                className="text-xs text-error-600"
              >
                Remove
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <TextField label="">
              <TextField.Input
                placeholder="https://bithomp.com/en/tx/..."
                value={newProofLink}
                onChange={(e) => setNewProofLink(e.target.value)}
              />
            </TextField>
            <Button variant="neutral-secondary" size="small" onClick={addProofLink}>Add</Button>
          </div>
          <Button variant="brand-primary" onClick={handleResolve} loading={resolving}>
            Resolve Contest
          </Button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Past Contest Summary
// ============================================================================

const PastContestSummary: React.FC<{ contestView: ContestView }> = ({ contestView }) => {
  const { contest, prizeTiers, stats, winners } = contestView;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-3 border border-neutral-border rounded-md">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-default-font">{contest.name}</span>
          <Badge variant="neutral">Resolved</Badge>
          <span className="text-xs text-subtext-color">
            {new Date(contest.resolved_at || contest.ends_at).toLocaleDateString()}
          </span>
        </div>
        <span className="text-xs text-subtext-color">{expanded ? 'Hide' : 'Show'}</span>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex gap-4 text-subtext-color">
            <span>{stats.entries} entries</span>
            <span>{stats.participants} participants</span>
          </div>
          {winners && winners.length > 0 && (
            <div className="space-y-1">
              <span className="text-subtext-color font-medium">Winners:</span>
              {winners.map(w => (
                <div key={w.id} className="flex items-center gap-2 pl-2">
                  <Badge variant="success" className="text-xs">{w.tier_name}</Badge>
                  <WalletAddress address={w.wallet_address} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
